import { ComponentStyleProps } from "../../../lib/styles/props/component-style-props";
import {
  ChangeBadgeProperties,
  CMButtonFormCommentProps,
  CMFormInputSearchAndSelect,
  FormRequiredLabelProps,
  SearchDropdownDisplayValue,
} from "@pscsrvlab/psc-react-components";
import { Document } from "../../../store/api/generated/stock-request-api";
import { ReactElement, useCallback, useState } from "react";
import { hasValue } from "../../../lib/util/common-util";
import log from "loglevel";
import { errorMessageOf } from "../../../lib/util/error-util";
import useCustomToast from "../../../hooks/use-custom-toast";
import { stockRequestApi as api } from "../../../store/api/enhanced-api";
import { useAppTranslation } from "../../../hooks/use-app-translation";

export type FormInputDocumentSelectProps = {
  editMode?: "editable" | "disabled" | "readOnly";
  size?: "sm" | "md" | "lg";
  noHeader?: boolean;
  label?: string;
  placeholder?: string;
  placeholderPopover?: string;
  projectId?: number;
  documentId?: number;
  hideClearButton?: boolean;
  labelSubmit?: string;
  labelClear?: string;
  selectButtonRightIcon?: ReactElement;
  popoverTitle?: string;
  searchResultNone?: string;
  changedFromDisplayValue?: string;
  onChange?: (value?: number) => void;
  requiredLabelProps?: FormRequiredLabelProps;
  changeBadgeProperties?: ChangeBadgeProperties<number>;
  checkEqualityFn?: (v0: number, v1: number) => boolean | Promise<boolean>;
  commentButtonProps?: CMButtonFormCommentProps;
  usePortal?: boolean;
} & ComponentStyleProps;

/**
 * 書類セレクトボックス(入力フォーム用)
 * @constructor
 */
export const FormInputDocumentSelect = ({
  editMode,
  size,
  noHeader,
  label,
  placeholder,
  placeholderPopover,
  projectId,
  documentId,
  hideClearButton,
  labelSubmit,
  labelClear,
  selectButtonRightIcon,
  popoverTitle,
  searchResultNone,
  changedFromDisplayValue,
  onChange,
  requiredLabelProps,
  changeBadgeProperties,
  checkEqualityFn,
  commentButtonProps,
  usePortal,
  sx,
  ...rest
}: FormInputDocumentSelectProps) => {
  const { t } = useAppTranslation();
  const { errorToast } = useCustomToast();

  // 書類情報一覧取得用遅延クエリ
  const [triggerListDocumentQuery] = api.useLazyListDocumentQuery();
  // 書類情報取得用遅延クエリ
  const [triggerGetDocumentQuery] = api.useLazyGetDocumentQuery();

  // 検索結果(退避用)
  const [searchResult, setSearchResult] = useState<Document[]>([]);

  // 表示用ラベルの取得
  const toDisplayValue = async (documentId: number) => {
    // 書類情報を取得し書類管理番号を返却
    const document = await getDocument(documentId);
    return {
      mainLabel: document?.documentControlNumber ?? "",
    } as SearchDropdownDisplayValue;
  };

  // 書類情報の取得
  const getDocument = useCallback(
    async (documentId: number): Promise<Document | undefined> => {
      try {
        // 検索結果の退避が無い場合はAPIから取得
        if (!searchResult.length) {
          return await triggerGetDocumentQuery({
            documentId: documentId,
          }).unwrap();
        }
        // 検索結果の退避が在る場合はそこから取得
        else {
          // 書類IDが一致するデータを検索結果(退避用)から抽出して返却
          return searchResult?.find((document) => document.id === documentId);
        }
      } catch (e) {
        log.error(errorMessageOf(e));
        errorToast(t("mes.汎用エラーメッセージ"));
      }
    },
    [errorToast, searchResult, t, triggerGetDocumentQuery],
  );

  // 検索時のイベントハンドラ
  const handleSearch = async (text: string) => {
    try {
      // 案件IDと書類管理番号(入力値)を元に書類を検索
      const data = await triggerListDocumentQuery({
        projectId: projectId,
        documentControlNumber: text,
      }).unwrap();
      // 検索結果を退避
      setSearchResult(data);
      // 書類IDのリストを返却
      return data.map((document) => document.id);
    } catch (e) {
      // 書類取得に失敗した場合は空のリストを返却
      log.error(errorMessageOf(e));
      errorToast(t("mes.汎用エラーメッセージ"));
      setSearchResult([]);
      return [];
    }
  };

  // 確定ボタン押下時のイベントハンドラ
  const handleChange = (documentId: number | null) => {
    // 親コンポーネントから渡されたイベントをトリガー
    onChange?.(hasValue(documentId) ? documentId : undefined);
  };

  return (
    <CMFormInputSearchAndSelect
      editMode={editMode}
      size={size}
      noHeader={noHeader}
      label={label ?? t("lbl.書類管理番号")}
      placeholder={placeholder}
      placeholderPopover={placeholderPopover}
      value={documentId}
      hideClearButton={hideClearButton}
      labelSubmit={labelSubmit ?? t("btn.確定ボタン")}
      labelClear={labelClear ?? t("btn.クリアボタン")}
      selectButtonRightIcon={selectButtonRightIcon}
      popoverTitle={popoverTitle ?? t("mes.書類選択案内メッセージ")}
      searchResultNone={searchResultNone ?? t("mes.該当書類なしメッセージ")}
      toDisplayValue={toDisplayValue}
      menuShowMaxCount={30}
      changedFromDisplayValue={changedFromDisplayValue}
      onSearch={handleSearch}
      onChange={handleChange}
      requiredLabelProps={requiredLabelProps}
      changeBadgeProperties={changeBadgeProperties}
      checkEqualityFn={checkEqualityFn}
      commentButtonProps={commentButtonProps}
      usePortal={usePortal}
      sx={sx}
      {...rest}
    />
  );
};
